import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { find, keyBy } from 'lodash'
import useNodeFeaturesResources from '../../../shared/resources/api/v1/admin/useNodeFeaturesResources'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import LoadingState from '../../../shared/components/LoadingState'
import PageSection from '../../../shared/components/layouts/PageSection'
import NodeFeatureCard from './NodeFeatureCard'
import { FormattedMessage } from 'react-intl'

const FEATURE_TYPES = ['docker', 'tailscale', 'fail2ban']

function NodeFeaturesTab({ teamSlug, nodeId, active }) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { openConfirmationDialog } = useConfirmationDialog()
  const { all, create, destroy } = useNodeFeaturesResources(teamSlug, nodeId)

  const [features, setFeatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})

  const loadFeatures = useCallback(() => {
    return all()
      .then((response) => {
        setFeatures(response.nodeFeatures || [])
      })
      .catch(() => {
        setFeatures([])
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSlug, nodeId])

  useEffect(() => {
    if (active) {
      loadFeatures()
    }
  }, [active, loadFeatures])

  const featuresByType = keyBy(features, 'featureType')

  const handleInstall = useCallback((featureType) => {
    setActionLoading((prev) => ({ ...prev, [featureType]: true }))
    create({ nodeFeature: { featureType } })
      .then(() => {
        enqueueSnackbar(formatMessage({ id: 'nodes.features.install.success' }), { variant: 'success' })
        return loadFeatures()
      })
      .catch(() => {
        enqueueSnackbar(formatMessage({ id: 'nodes.features.install.error' }), { variant: 'error' })
      })
      .finally(() => {
        setActionLoading((prev) => ({ ...prev, [featureType]: false }))
      })
  }, [create, loadFeatures, enqueueSnackbar, formatMessage])

  const handleUninstall = useCallback((feature) => {
    openConfirmationDialog({
      title: <FormattedMessage id="nodes.features.delete.title" />,
      message: <FormattedMessage id="nodes.features.delete.message" />,
      onConfirm: () => {
        setActionLoading((prev) => ({ ...prev, [feature.featureType]: true }))
        return destroy(feature.id)
          .then(() => {
            enqueueSnackbar(formatMessage({ id: 'nodes.features.delete.success' }), { variant: 'success' })
            return loadFeatures()
          })
          .catch(() => {
            enqueueSnackbar(formatMessage({ id: 'nodes.features.delete.error' }), { variant: 'error' })
          })
          .finally(() => {
            setActionLoading((prev) => ({ ...prev, [feature.featureType]: false }))
          })
      },
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, loadFeatures, enqueueSnackbar, formatMessage])

  if (!active) {
    return null
  }

  if (loading) {
    return <LoadingState />
  }

  return (
    <PageSection titleId="nodes.features.title">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {FEATURE_TYPES.map((featureType) => (
          <NodeFeatureCard
            key={featureType}
            featureType={featureType}
            feature={featuresByType[featureType]}
            onInstall={handleInstall}
            onUninstall={handleUninstall}
            loading={actionLoading[featureType]}
          />
        ))}
      </Box>
    </PageSection>
  )
}

export default NodeFeaturesTab
